import React, { useState, useEffect } from 'react';
import { X, ShieldCheck } from 'lucide-react';

const STORAGE_KEY = 'senritsux_analytics_consent';

/**
 * SenritsuX Cookie Consent Banner
 *
 * PostHog analytics is active on this site. Under GDPR and India's DPDP Act 2023,
 * users should have the opportunity to opt out of behavioral analytics tracking.
 *
 * This banner:
 * - Only appears on first visit (checks localStorage)
 * - Does NOT block Supabase auth or essential functionality
 * - On "Decline": calls posthog.opt_out_capturing() if posthog is loaded
 * - On "Accept": stores preference, posthog continues normally
 * - Matches the existing SenritsuX dark UI design system
 */
export default function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        // Slight delay so it doesn't flash instantly on load
        const timer = setTimeout(() => setVisible(true), 1500);
        return () => clearTimeout(timer);
      }
    } catch {
      // localStorage unavailable (private mode, etc.) ,  don't show banner
    }
  }, []);

  const handleAccept = () => {
    try {
      localStorage.setItem(STORAGE_KEY, 'accepted');
    } catch {}
    setVisible(false);
  };

  const handleDecline = () => {
    try {
      localStorage.setItem(STORAGE_KEY, 'declined');
      // Opt PostHog out of capturing if it's loaded
      if (typeof window !== 'undefined' && window.posthog && typeof window.posthog.opt_out_capturing === 'function') {
        window.posthog.opt_out_capturing();
      }
    } catch {}
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label="Cookie and analytics consent"
      aria-live="polite"
      style={{
        position: 'fixed',
        bottom: '1.25rem',
        left: '1.25rem',
        right: '1.25rem',
        maxWidth: '520px',
        margin: '0 auto',
        zIndex: 9000,
        background: 'rgba(18, 16, 48, 0.97)',
        border: '1px solid rgba(217, 251, 6, 0.25)',
        borderRadius: '14px',
        padding: '1.25rem 1.4rem',
        boxShadow: '0 8px 40px rgba(0,0,0,0.55)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.85rem',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.65rem' }}>
        <ShieldCheck
          size={20}
          color="#d9fb06"
          aria-hidden="true"
          style={{ flexShrink: 0, marginTop: '2px' }}
        />
        <div style={{ flex: 1 }}>
          <p style={{ margin: 0, color: '#fff', fontWeight: 700, fontSize: '0.92rem', lineHeight: 1.4 }}>
            Analytics & Privacy
          </p>
          <p style={{ margin: '0.3rem 0 0', color: 'rgba(255,255,255,0.65)', fontSize: '0.82rem', lineHeight: 1.5, textWrap: 'pretty' }}>
            SenritsuX uses analytics to understand how artists use the platform and improve your experience.
            No personal data is sold. You can opt out at any&nbsp;time.
          </p>
        </div>
        <button
          type="button"
          onClick={handleDecline}
          aria-label="Decline and close"
          style={{
            background: 'transparent',
            border: 'none',
            color: 'rgba(255,255,255,0.4)',
            cursor: 'pointer',
            padding: '0.15rem',
            flexShrink: 0,
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.85)')}
          onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.4)')}
        >
          <X size={16} aria-hidden="true" />
        </button>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={handleDecline}
          style={{
            padding: '0.45rem 1rem',
            borderRadius: '8px',
            background: 'rgba(255,255,255,0.07)',
            border: '1px solid rgba(255,255,255,0.18)',
            color: 'rgba(255,255,255,0.75)',
            fontSize: '0.82rem',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'background 0.15s',
            whiteSpace: 'nowrap',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.12)')}
          onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.07)')}
        >
          Decline
        </button>
        <button
          type="button"
          onClick={handleAccept}
          style={{
            padding: '0.45rem 1.1rem',
            borderRadius: '8px',
            background: '#d9fb06',
            border: '1px solid #d9fb06',
            color: '#111',
            fontSize: '0.82rem',
            fontWeight: 700,
            cursor: 'pointer',
            transition: 'background 0.15s, transform 0.1s',
            whiteSpace: 'nowrap',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#c8eb00';
            e.currentTarget.style.transform = 'translateY(-1px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#d9fb06';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          Accept Analytics
        </button>
      </div>
    </div>
  );
}
